import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { CustomIcon } from '../CustomIcon/CustomIcon';
import { SearchAutocomplete } from './SearchAutocomplete';
import './SearchBar.scss';

/**
 * SearchBar - Search input with form submission for MegaMenu
 *
 * Renders a search form that submits to a configurable results path.
 *
 * Features:
 * - Form submission navigates to results path with query param
 * - Optional cancel button to close search
 * - Placeholder text for CMS translation
 * - Opt-in autocomplete dropdown (`enableAutocomplete`, default off) rendered by
 *   the sibling SearchAutocomplete component
 * - The autocomplete's two screen-reader strings (list label and count
 *   announcement) are author-supplied props, so a localized document does not
 *   announce English
 * - Self-contained: receives all data as props, no knowledge of siblings
 */

/** Minimum characters before querying suggestions, matching legacy autocomplete. */
const DEFAULT_AUTOCOMPLETE_MIN_CHARS = 3;
/** Debounce in ms before querying suggestions, matching legacy autocomplete. */
const DEFAULT_AUTOCOMPLETE_THROTTLE_MS = 800;

/**
 * Whether the current page is the results page, so the submitted query belongs in the input.
 * Jellyfish routes every path whose last segment is the results segment to the results page,
 * which covers locale prefixes, so a whole trailing path segment matches at any depth:
 * `/es/search` and `/a/b/search` match `/search`, `/research` does not (the target pathname
 * always starts with `/`, which pins the segment boundary).
 * `resultsPath` is an authored Url and may be absolute, so only its pathname is compared.
 * Jellyfish serves the results page with or without a trailing slash, so one trailing slash
 * is ignored on both sides.
 */
function matchesResultsPath(pathname: string, resultsPath: string): boolean {
  let target: string;
  try {
    target = stripTrailingSlash(new URL(resultsPath, window.location.origin).pathname);
  } catch {
    return false;
  }
  const current = stripTrailingSlash(pathname);
  if (target === '/') {
    return current === '/';
  }
  return current === target || current.endsWith(target);
}

function stripTrailingSlash(path: string): string {
  return path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path;
}

/**
 * @polySemanticUsage About this block: the site search input. It ships as part of the MegaMenu
 * overlay and can also be placed on its own (for example at the top of a search results page),
 * where turning on autocomplete adds typeahead suggestions. Migration rule: do not author this
 * block to reproduce a header search box the MegaMenu already renders.
 */
export interface SearchBarProps {
  /**
   * Search input placeholder text
   */
  placeholder?: string;
  /**
   * Search results page path
   * @polyType Url
   * @polyLabel Results Page
   */
  resultsPath?: string;
  /**
   * Callback when search should close (cancel button). Programmatic only.
   * @polyIgnore
   */
  onCancel?: () => void;
  /**
   * Whether to show the cancel/close button
   * @polyLabel Show Close Button
   */
  showCancel?: boolean;
  /**
   * Focus the input as soon as it mounts. Off by default: a standalone block on
   * a page would steal focus and scroll on load (WCAG 2.4.3). The MegaMenu
   * overlay opts in, since opening it is a request to type. Programmatic only.
   * @polyIgnore
   */
  focusOnMount?: boolean;
  /**
   * Enable the autocomplete suggestions dropdown. Default false (MegaMenu overlay stays unchanged).
   */
  enableAutocomplete?: boolean;
  /**
   * Endpoint queried for suggestions. Default '/autocomplete'.
   * @polyType Url
   */
  autocompleteEndpoint?: string;
  /**
   * Accessible name for the suggestion list, read out by screen readers.
   * @polyLabel Autocomplete Suggestions Label
   * @polyHelp Names the suggestion list for screen-reader users. Leave empty for the built-in wording, "Search suggestions".
   */
  autocompleteListboxLabel?: string;
  /**
   * Screen-reader announcement made when suggestions appear.
   * @polyHelp Announced when suggestions appear. Write {count} where the number of suggestions belongs. Leave empty for the built-in English wording, "{count} suggestions available, use the up and down arrow keys to review", which also handles the single-suggestion case.
   */
  autocompleteAnnouncement?: string;
  /**
   * Minimum characters before querying. Default 3, matching the legacy autocomplete: a
   * parity constant rather
   * than an author-facing lever, so it stays a prop for composition and is hidden
   * from the CMS. Programmatic only.
   * @polyIgnore
   */
  autocompleteMinChars?: number;
  /**
   * Debounce in ms before querying. Default 800, matching the legacy autocomplete: a
   * parity constant rather
   * than an author-facing lever, so it stays a prop for composition and is hidden
   * from the CMS. Programmatic only.
   * @polyIgnore
   */
  autocompleteThrottleMs?: number;
}

export const SearchBar: React.FC<SearchBarProps> = ({
  placeholder = '',
  resultsPath = '/search',
  onCancel,
  showCancel = false,
  focusOnMount = false,
  enableAutocomplete = false,
  autocompleteEndpoint = '/autocomplete',
  autocompleteListboxLabel,
  autocompleteAnnouncement,
  autocompleteMinChars = DEFAULT_AUTOCOMPLETE_MIN_CHARS,
  autocompleteThrottleMs = DEFAULT_AUTOCOMPLETE_THROTTLE_MS,
}) => {
  const [query, setQuery] = useState('');
  const [isListboxOpen, setIsListboxOpen] = useState(false);
  const [activeOptionId, setActiveOptionId] = useState<string | null>(null);
  const [isSubmitPending, setIsSubmitPending] = useState(false);
  const [suppressAutocomplete, setSuppressAutocomplete] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const instanceId = useId();
  const listboxId = `poly-search-autocomplete-${instanceId}`;
  const prefillApplied = useRef(false);

  // After mount so the server render and the first client render agree on an empty input,
  // and only once so a later prop change cannot overwrite what the user has typed.
  useEffect(() => {
    if (prefillApplied.current) return;
    prefillApplied.current = true;
    const typedValue = inputRef.current?.value;
    if (typedValue) {
      setQuery(typedValue);
      return;
    }
    if (!matchesResultsPath(window.location.pathname, resultsPath)) return;
    const prefill = new URLSearchParams(window.location.search).get('search');
    if (prefill) {
      setQuery(prefill);
      setSuppressAutocomplete(true);
    }
  }, [resultsPath]);

  // Focus input on mount, only where the caller asked for it
  useEffect(() => {
    if (!focusOnMount) return;
    inputRef.current?.focus();
  }, [focusOnMount]);

  // A chosen suggestion submits only after React has committed it to the input,
  // so the GET carries the suggestion rather than the typed prefix.
  useEffect(() => {
    if (!isSubmitPending) return;
    setIsSubmitPending(false);
    const form = formRef.current;
    if (!form) return;
    // requestSubmit is unavailable below Safari 16, which is inside the build's
    // browser floor; submit() skips onSubmit but a chosen suggestion is never
    // empty, so the only thing that guard would have done does not apply.
    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit();
    } else {
      form.submit();
    }
  }, [isSubmitPending]);

  const handleSubmit = (e: React.FormEvent) => {
    if (!query.trim()) {
      e.preventDefault();
    }
  };

  const handleSelectSuggestion = useCallback((suggestion: string) => {
    setQuery(suggestion);
    setIsListboxOpen(false);
    setActiveOptionId(null);
    setIsSubmitPending(true);
  }, []);

  // Combobox wiring is applied only in autocomplete mode; with the flag off the
  // input keeps its implicit searchbox role and its original attribute set.
  const autocompleteAriaProps = enableAutocomplete
    ? {
        role: 'combobox',
        'aria-autocomplete': 'list' as const,
        'aria-expanded': isListboxOpen,
        ...(isListboxOpen ? { 'aria-controls': listboxId } : {}),
        ...(activeOptionId ? { 'aria-activedescendant': activeOptionId } : {}),
      }
    : undefined;

  return (
    <form
      ref={formRef}
      className="poly-search-bar"
      action={resultsPath}
      method="get"
      role="search"
      onSubmit={handleSubmit}
    >
      <div className="poly-search-bar__container">
        <CustomIcon name="search" className="poly-search-bar__icon" aria-hidden="true" />
        <input
          ref={inputRef}
          type="search"
          name="search"
          className={`poly-search-bar__input${showCancel ? ' poly-search-bar__input--with-cancel' : ''}`}
          placeholder={placeholder}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            if (suppressAutocomplete) {
              setSuppressAutocomplete(false);
            }
          }}
          autoComplete="off"
          spellCheck={false}
          aria-label={placeholder || 'Search'}
          {...autocompleteAriaProps}
        />
        {showCancel && onCancel && (
          <button
            type="button"
            className="poly-search-bar__cancel"
            onClick={onCancel}
            aria-label="Close search"
          >
            <CustomIcon name="close" className="poly-search-bar__cancel-icon" aria-hidden="true" />
          </button>
        )}
        {enableAutocomplete && (
          <SearchAutocomplete
            query={query}
            endpoint={autocompleteEndpoint}
            minChars={autocompleteMinChars}
            throttleMs={autocompleteThrottleMs}
            listboxId={listboxId}
            inputRef={inputRef}
            listboxLabel={autocompleteListboxLabel}
            announcementTemplate={autocompleteAnnouncement}
            onSelect={handleSelectSuggestion}
            onOpenChange={setIsListboxOpen}
            onActiveOptionChange={setActiveOptionId}
            suppressQuery={suppressAutocomplete}
          />
        )}
      </div>
    </form>
  );
};
